import { FC, useCallback } from "react";
import { ChatServerMessage } from "../..";
import { MessageMiddlewareContext } from "../../middlewares";
import { ChatMsgDetail } from "../../store/ChatAction";
import { isAgent } from "../../utils";
import { ActionButton } from "../ActionButton";

export interface ChatPermissionMessageProps {
    readonly message: ChatMsgDetail;
    readonly sibling: boolean;
    readonly ctx: MessageMiddlewareContext;
    readonly time?: string;
}

export const ChatPermissionMessage: FC<ChatPermissionMessageProps> = (props) => {
    const { message, ctx } = props;

    if (!ctx) {
        throw new TypeError(`ctx not provided to ${ChatPermissionMessage.name}`);
    }

    const { user } = ctx;

    const permissionRequest = message.msg.permissionRequest;

    const agentMessage = isAgent(props.message.user.nick);

    const { user: author } = message;

    const allowLabel = "Allow";
    const denyLabel = "Deny";

    const writeAsAgent = useCallback(
        (msg: ChatServerMessage) => {
            ctx.write({
                type: "msg",
                user: author,
                msg,
            });
        },
        [ctx, author]
    );

    const writeAsUser = useCallback(
        (msg: ChatServerMessage) => {
            ctx.write({
                type: "msg",
                user: user,
                msg,
            });
        },
        [ctx, user]
    );

    const handleFail = useCallback(() => {
        ctx.send({
            type: "msg",
            user: undefined,
            msg: {
                text: denyLabel,
            },
        });
    }, [ctx]);

    const handleSend = useCallback(
        (msg: ChatServerMessage) => {
            ctx.send({
                type: "permissionGrant",
                user: author,
                msg,
            });
        },
        [author, ctx]
    );

    const handleResult = useCallback(
        (position: GeolocationPosition | undefined, userMsg: string) => {
            handleSend({
                text: userMsg,
                location: position
                    ? {
                          latitude: position.coords.latitude,
                          longitude: position.coords.longitude,
                      }
                    : undefined,
                permissionRequest,
            });
            writeAsUser({
                text: userMsg,
            });
            const agentMsg = position ? permissionRequest?.approve : permissionRequest?.deny;
            if (agentMsg) {
                writeAsAgent(agentMsg);
            }
        },
        [permissionRequest, writeAsUser, writeAsAgent, handleSend]
    );

    const handleDeny = useCallback(
        (label: string) => {
            handleResult(undefined, label);
        },
        [handleResult]
    );

    const handleAllow = useCallback(
        (label: string) => {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        handleResult(position, label);
                    },
                    () => {
                        handleFail();
                    }
                );
            }
        },
        [handleFail, handleResult]
    );

    if (!permissionRequest) {
        return <></>;
    }

    return (
        <div className="chat-msg">
            <span className="message-sr-only">
                {"at " + props.time + (agentMessage ? " the bot said" : " the user said")}
            </span>
            <div className="buttons-container">
                <ActionButton label={allowLabel} addClass="button" onClick={handleAllow} />
                <ActionButton label={denyLabel} addClass="button" onClick={handleDeny} />
            </div>
        </div>
    );
};
